package fr.frux.feature.support

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.VolunteerActivism
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import fr.frux.app.resources.Res
import fr.frux.app.resources.support_prompt_amount_10
import fr.frux.app.resources.support_prompt_amount_20
import fr.frux.app.resources.support_prompt_amount_5
import fr.frux.app.resources.support_prompt_close_description
import fr.frux.app.resources.support_prompt_context_contribution
import fr.frux.app.resources.support_prompt_context_harvest
import fr.frux.app.resources.support_prompt_link_out_cta
import fr.frux.app.resources.support_prompt_opt_out
import fr.frux.app.resources.support_prompt_other_amount
import fr.frux.app.resources.support_prompt_title
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.components.FruxModalBottomSheet
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.shared.domain.model.SupportPrompt
import fr.frux.shared.domain.model.SupportReason
import fr.frux.shared.domain.model.SupportVariant
import org.jetbrains.compose.resources.stringResource

/** Paliers proposés par la variante `INLINE` (spec 2026-08-13) : jamais de formulaire de paiement. */
private val INLINE_AMOUNTS_EUROS = listOf(5, 10, 20)

/**
 * Feuille de soutien (spec 2026-08-13, tâche 11) : sollicitation ponctuelle après un signalement ou
 * une récolte, jamais un écran, jamais de formulaire de paiement. `onDonate` reçoit l'URL déjà
 * résolue (montant présélectionné pour une pastille, URL nue pour « Autre montant » ou pour la
 * variante `LINK_OUT`) : l'ouverture effective reste la responsabilité de l'appelant (host).
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SupportPromptSheet(
    prompt: SupportPrompt,
    onDonate: (String) -> Unit,
    onDismiss: () -> Unit,
    onOptOut: () -> Unit,
) {
    FruxModalBottomSheet(onDismiss = onDismiss, skipPartiallyExpanded = true) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = FruxSpacing.md)
                    .padding(bottom = FruxSpacing.lg),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    imageVector = Icons.Outlined.VolunteerActivism,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                )
                Spacer(modifier = Modifier.width(FruxSpacing.sm))
                Text(
                    text = stringResource(Res.string.support_prompt_title),
                    style = MaterialTheme.typography.titleLarge,
                    modifier = Modifier.weight(1f),
                )
                FruxIconButton(
                    icon = Icons.Outlined.Close,
                    contentDescription = stringResource(Res.string.support_prompt_close_description),
                    onClick = onDismiss,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            Text(
                text =
                    when (prompt.reason) {
                        SupportReason.CONTRIBUTION -> stringResource(Res.string.support_prompt_context_contribution)
                        SupportReason.HARVEST -> stringResource(Res.string.support_prompt_context_harvest)
                    },
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            Spacer(modifier = Modifier.height(FruxSpacing.sm))

            when (prompt.variant) {
                SupportVariant.LINK_OUT -> {
                    FruxButton(
                        text = stringResource(Res.string.support_prompt_link_out_cta),
                        onClick = { onDonate(prompt.donateUrl) },
                        style = FruxButtonStyle.Filled,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }

                SupportVariant.INLINE -> {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                    ) {
                        val amountLabels =
                            listOf(
                                stringResource(Res.string.support_prompt_amount_5),
                                stringResource(Res.string.support_prompt_amount_10),
                                stringResource(Res.string.support_prompt_amount_20),
                            )
                        INLINE_AMOUNTS_EUROS.forEachIndexed { index, amount ->
                            FruxButton(
                                text = amountLabels[index],
                                onClick = { onDonate("${prompt.donateUrl}&amount=$amount") },
                                style = FruxButtonStyle.Outline,
                                modifier = Modifier.weight(1f),
                            )
                        }
                    }
                    TextButton(onClick = { onDonate(prompt.donateUrl) }) {
                        Text(text = stringResource(Res.string.support_prompt_other_amount))
                    }
                }
            }

            TextButton(onClick = onOptOut) {
                Text(
                    text = stringResource(Res.string.support_prompt_opt_out),
                    style = MaterialTheme.typography.bodySmall,
                )
            }
        }
    }
}
